import React, { useEffect, useState } from "react";

import { SignUpFormProps } from "~/shared/utils/types";
import { useSignUpStore } from "~/stores";
import {
  getAutoSelectedPlanType,
  getNextInsuranceStep,
  getPrevInsuranceStep,
  INSURANCE_FORM_STEPS,
} from "~/utils";
import { MemberIdForm } from "./Insurance";
import { AetnaPlanForm } from "./Insurance/AetnaPlanForm";
import { AnthemEmpirePosForm } from "./Insurance/AnthemEmpirePosForm";
import { BcbsPlanForm } from "./Insurance/BcbsPlanForm";
import { ConfirmSecondaryInsuranceForm } from "./Insurance/ConfirmSecondaryInsuranceForm";
import { FidelisPlanForm } from "./Insurance/FidelisPlanForm";
import { MedicalConditionsForm } from "./Insurance/MedicalConditionsForm";
import { OtherInsuranceProviderForm } from "./Insurance/OtherInsuranceProvider";
import { PlanCoverageForm } from "./Insurance/PlanCoverageForm";
import { PlanTypeProviderForm } from "./Insurance/PlanTypeProviderForm";
import { ProviderForm } from "./Insurance/Provider";
import { UhcPlanForm } from "./Insurance/UhcPlanCoverageForm";

const formSteps: Record<INSURANCE_FORM_STEPS, React.ComponentType<any>> = {
  [INSURANCE_FORM_STEPS.PROVIDER]: ProviderForm,
  [INSURANCE_FORM_STEPS.OTHER_INSURANCE]: OtherInsuranceProviderForm,
  [INSURANCE_FORM_STEPS.PLAN_TYPE_PROVIDER]: PlanTypeProviderForm,
  [INSURANCE_FORM_STEPS.MEDICAL_CONDITIONS]: MedicalConditionsForm,
  [INSURANCE_FORM_STEPS.PLAN_COVERAGE]: PlanCoverageForm,
  [INSURANCE_FORM_STEPS.BCBS_PLAN]: BcbsPlanForm,
  // Unreachable here — the ANTHEM_EMPIRE_POS step is gated to the primary insurance
  // (isSecondary: false in insuranceFlows.json) — but the Record must be exhaustive.
  [INSURANCE_FORM_STEPS.ANTHEM_EMPIRE_POS]: AnthemEmpirePosForm,
  [INSURANCE_FORM_STEPS.UHC_PLAN]: UhcPlanForm,
  [INSURANCE_FORM_STEPS.AETNA_PLAN]: AetnaPlanForm,
  [INSURANCE_FORM_STEPS.FIDELIS_PLAN]: FidelisPlanForm,
  [INSURANCE_FORM_STEPS.MEMBER_ID_FORM]: MemberIdForm,
};

export const SecondaryInsuranceProviderForm = ({
  onPrev,
  onNext,
}: SignUpFormProps) => {
  const [currentForm, setCurrentForm] = useState<keyof typeof formSteps>(
    INSURANCE_FORM_STEPS.PROVIDER,
  );
  const [showSecondaryInsuranceForm, setShowSecondaryInsuranceForm] =
    useState(false);
  const {
    patient,
    showInsuranceFinalStep,
    setShowInsuranceFinalStep,
    setPatientField,
    setPatientInsuranceField,
  } = useSignUpStore();
  const { secondaryInsurance, state } = patient;

  useEffect(() => {
    if (showInsuranceFinalStep) {
      setCurrentForm(INSURANCE_FORM_STEPS.MEMBER_ID_FORM);
      setShowInsuranceFinalStep(false);
      setShowSecondaryInsuranceForm(true);
    }
  }, []);

  const onNextInsuranceStep = (response?: string) => {
    const effectiveProvider =
      currentForm === INSURANCE_FORM_STEPS.PROVIDER
        ? response
        : secondaryInsurance?.providerName;

    // Auto-set plan type when provider has only one option
    if (currentForm === INSURANCE_FORM_STEPS.PROVIDER) {
      const autoPlanType = getAutoSelectedPlanType(effectiveProvider);
      if (autoPlanType) {
        setPatientInsuranceField("planTypeProvider", autoPlanType, true);
      }
    }

    const nextStep = getNextInsuranceStep(
      currentForm,
      response,
      secondaryInsurance?.providerName,
      secondaryInsurance?.planTypeProvider,
      state,
      secondaryInsurance?.planCoverage,
      secondaryInsurance?.bcbsProvider,
      true,
    );
    if (nextStep) {
      setCurrentForm(nextStep);
    } else {
      onNext();
    }
  };

  const onPrevInsuranceStep = () => {
    if (
      currentForm === INSURANCE_FORM_STEPS.PROVIDER &&
      !showSecondaryInsuranceForm
    ) {
      onPrev();
    } else if (currentForm === INSURANCE_FORM_STEPS.PROVIDER) {
      setShowSecondaryInsuranceForm(false);
    } else {
      const prevStep = getPrevInsuranceStep(
        currentForm,
        secondaryInsurance?.providerName!,
        secondaryInsurance?.otherProvider,
        secondaryInsurance?.planTypeProvider,
        state,
        secondaryInsurance?.planCoverage,
        secondaryInsurance?.bcbsProvider,
        true,
      );
      if (prevStep) setCurrentForm(prevStep);
    }
  };

  const CurrentComponent = formSteps[currentForm];

  return (
    <>
      {!showSecondaryInsuranceForm ? (
        <ConfirmSecondaryInsuranceForm
          onPrev={onPrev}
          onNext={(value) => {
            if (value === "Yes") {
              setShowSecondaryInsuranceForm(true);
            } else {
              setPatientField("secondaryInsurance", undefined);
              onNext();
            }
          }}
        />
      ) : (
        <CurrentComponent
          onPrev={onPrevInsuranceStep}
          onNext={onNextInsuranceStep}
          isSecondaryProvider
        />
      )}
    </>
  );
};
